
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebaseService";
import { Coordinates, SvvCamera } from "../types";
import { getDistance, isPointNearPolyline } from "./geometryUtils";
import { getCache, setCache, CACHE_TTL } from "./cacheService";

const CACHE_KEY_PREFIX = 'svv_cameras_';

/**
 * Fetch online cameras near a route polyline (within bufferMeters).
 */
export const fetchCamerasNearRoute = async (
  routePoints: Coordinates[],
  bufferMeters = 2000
): Promise<SvvCamera[]> => {
  if (!routePoints || routePoints.length < 2) return [];

  const lats = routePoints.map(p => p.latitude);
  const lons = routePoints.map(p => p.longitude);
  const bufferDeg = bufferMeters / 111000;
  const latMin = Math.min(...lats) - bufferDeg;
  const latMax = Math.max(...lats) + bufferDeg;
  const lonMin = Math.min(...lons) - bufferDeg;
  const lonMax = Math.max(...lons) + bufferDeg;

  const cacheKey = `${CACHE_KEY_PREFIX}route_${latMin.toFixed(3)}_${latMax.toFixed(3)}_${lonMin.toFixed(3)}_${lonMax.toFixed(3)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached as SvvCamera[];

  try {
    const camerasRef = collection(db, "svv_cameras");
    const q = query(
      camerasRef,
      where("is_online", "==", true),
      where("latitude", ">=", latMin),
      where("latitude", "<=", latMax)
    );

    const snapshot = await getDocs(q);
    const cameras: SvvCamera[] = [];

    snapshot.forEach(doc => {
      const data = doc.data() as SvvCamera;
      if (data.longitude < lonMin || data.longitude > lonMax) return;
      const point: Coordinates = { latitude: data.latitude, longitude: data.longitude };
      if (isPointNearPolyline(point, routePoints, bufferMeters)) {
        cameras.push(data);
      }
    });

    setCache(cacheKey, cameras, CACHE_TTL.SHORT);
    return cameras;
  } catch (err) {
    console.warn('[svvCameraService] fetchCamerasNearRoute failed:', err);
    return [];
  }
};

/**
 * Fetch online cameras near a spot (within radiusMeters).
 */
export const fetchCamerasNearSpot = async (
  coords: Coordinates,
  radiusMeters = 500
): Promise<(SvvCamera & { _distance: number })[]> => {
  if (!coords) return [];

  const bufferDeg = radiusMeters / 111000;
  const latMin = coords.latitude - bufferDeg;
  const latMax = coords.latitude + bufferDeg;
  const lonMin = coords.longitude - bufferDeg * 2;
  const lonMax = coords.longitude + bufferDeg * 2;

  const cacheKey = `${CACHE_KEY_PREFIX}spot_${coords.latitude.toFixed(4)}_${coords.longitude.toFixed(4)}_${radiusMeters}`;
  const cached = getCache(cacheKey);
  if (cached) return cached as (SvvCamera & { _distance: number })[];

  try {
    const camerasRef = collection(db, "svv_cameras");
    const q = query(
      camerasRef,
      where("is_online", "==", true),
      where("latitude", ">=", latMin),
      where("latitude", "<=", latMax)
    );

    const snapshot = await getDocs(q);
    const cameras: (SvvCamera & { _distance: number })[] = [];

    snapshot.forEach(doc => {
      const data = doc.data() as SvvCamera;
      if (data.longitude < lonMin || data.longitude > lonMax) return;
      const dist = getDistance(coords.latitude, coords.longitude, data.latitude, data.longitude);
      if (dist <= radiusMeters) {
        cameras.push({ ...data, _distance: Math.round(dist) });
      }
    });

    cameras.sort((a, b) => a._distance - b._distance);
    setCache(cacheKey, cameras, CACHE_TTL.SHORT);
    return cameras;
  } catch (err) {
    console.warn('[svvCameraService] fetchCamerasNearSpot failed:', err);
    return [];
  }
};
